/**
 * Detail panel shown when a marker is clicked.
 *
 * Extracted from MapPage to keep the page-level component focused on
 * Leaflet/state wiring. Pure presentational + two callbacks.
 */
import { X } from "lucide-react";
import { CATEGORY_COLORS, tileToGame, prettySubtype, prettyCategory } from "./markerIcons";

const CDN = process.env.REACT_APP_BACKEND_URL;

// Import bookkeeping / raw coordinate keys — useful in the DB, noise in the
// popup. Everything else still renders in the generic list below.
const NOISY_META = new Set([
  "source", "source_region", "source_layer", "z", "map", "pal_asset",
  "thgl_id", "thgl_type", "world_x", "world_y", "world_z",
  "arena_x", "arena_y", "paldeck_name", "boss_id",
]);

const META_LABELS = {
  sub: "Region", fish: "Tier", resource: "Node", relic: "Relic",
  pal: "Pal", level: "Level", bosses: "Bosses", syndicate: "Syndicate",
};

// Keys surfaced as summary chips under the title — skipped in the generic
// list below so the popup doesn't say "Lv 25" twice.
const CHIP_META = new Set(["level", "fish", "resource", "relic", "sub"]);

/** The short chips shown under the title — the facts players actually
 *  want (alpha level, region, fishing tier, node type). */
function summaryChips(marker) {
  const md = marker.metadata || {};
  const cat = marker.category;
  const out = [];
  if (md.level) out.push(`Lv ${md.level}`);
  if (md.fish) out.push(`${prettySubtype(md.fish, cat)} tier`);
  if (md.resource) out.push(prettySubtype(md.resource, cat));
  if (md.relic) out.push(`${prettySubtype(md.relic, cat)} effigy`);
  if (md.sub) out.push(prettySubtype(md.sub, cat));
  return out;
}

export function MarkerPopup({ marker, user, onClose, onToggleCollected, onCallTowerRun }) {
  if (!marker) return null;
  const color = CATEGORY_COLORS[marker.category] || "#F9F6EE";
  const heroSrc = marker.icon_url ? `${CDN}${marker.icon_url}` : null;
  const chips = summaryChips(marker);
  const metaRows = Object.entries(marker.metadata || {}).filter(
    ([k, v]) => !NOISY_META.has(k) && !CHIP_META.has(k) && v !== null && v !== "",
  );

  return (
    // Phones get a bottom sheet anchored to the *real* viewport (`fixed`),
    // not to the map container — the old `absolute bottom-3` put it below
    // the fold and dropped it straight onto Leaflet's bottom-right zoom
    // control and the chat bubble. Desktop keeps the top-right card.
    <div
      className="fixed inset-x-0 bottom-0 rounded-b-none lg:absolute lg:inset-x-auto lg:right-3 lg:top-3 lg:bottom-auto lg:rounded-[var(--radius)] z-[450] glass-card p-5 pb-8 lg:pb-5 w-full lg:w-80 lg:max-w-[calc(100vw-24px)] max-h-[70dvh] lg:max-h-[calc(100vh-120px)] overflow-y-auto"
      data-testid="marker-popup"
    >
      {/* Hero image — a bigger preview of the marker's icon. Sized to ~140px
          tall, contained so portraits keep their aspect ratio, framed with
          the category color so the user gets an at-a-glance category cue. */}
      {heroSrc && (
        <div
          className="relative mb-4 -mx-2 rounded overflow-hidden flex items-center justify-center bg-black/40"
          style={{
            height: 140,
            border: `1px solid ${color}55`,
            boxShadow: `inset 0 0 24px ${color}22`,
          }}
          data-testid="marker-hero-img"
        >
          <img
            src={heroSrc}
            alt={marker.name}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      )}

      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="badge mb-1" style={{ color, borderColor: color }}>
            {prettyCategory(marker.category)}
          </div>
          <h3 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{marker.name}</h3>
        </div>
        <button
          onClick={onClose}
          className="grid place-items-center h-11 w-11 -mr-2 -mt-2 lg:h-auto lg:w-auto lg:m-0 shrink-0 text-[var(--muted)] hover:text-[var(--bone)]"
          aria-label="Close marker details"
          data-testid="close-marker-popup"
        >
          <X size={18} />
        </button>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="marker-summary-chips">
          {chips.map((c) => (
            <span
              key={c}
              className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border"
              style={{ color, borderColor: `${color}55`, background: `${color}11` }}
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {marker.description && marker.description !== marker.name && (
        <p className="text-sm text-[var(--muted)] mb-3">{marker.description}</p>
      )}

      {marker.category === "anti_air_raid" && (
        <div
          className="mb-3 px-2 py-1.5 text-xs rounded border flex items-start gap-2"
          style={{
            borderColor: `${color}55`,
            background: `${color}11`,
            color: "#FFD7CC",
          }}
          data-testid="hostile-defense-badge"
        >
          <span aria-hidden="true">⚠</span>
          <span>
            <strong>Hostile defenses.</strong> Anti-air missile turret —
            bring ground transport or a high-mobility pal.
          </span>
        </div>
      )}

      <div className="text-[10px] text-[var(--muted)] font-mono mb-3 leading-tight" data-testid="marker-coords">
        {(() => {
          const g = tileToGame(marker.x, marker.y);
          return <div>in-game: <span className="text-[var(--bone)]">{g.x}, {g.y}</span></div>;
        })()}
      </div>

      {metaRows.length > 0 && (
        <ul className="text-xs text-[var(--muted)] space-y-1 mb-3" data-testid="marker-metadata">
          {metaRows.map(([k, v]) => (
            <li key={k}>
              <span className="text-[var(--bone)]">{META_LABELS[k] || k}:</span> {String(v)}
            </li>
          ))}
        </ul>
      )}

      {user ? (
        <div className="space-y-2">
          <button
            onClick={() => onToggleCollected(marker.id, marker.collected)}
            className={`btn w-full ${marker.collected ? "btn-secondary" : ""}`}
            data-testid="toggle-collected-button"
          >
            {marker.collected ? "Unmark" : "Mark as Found"}
          </button>
          {marker.category === "boss_tower" && (
            <button
              onClick={() => onCallTowerRun(marker.id)}
              className="btn btn-secondary w-full"
              data-testid="tower-run-button"
              title="Post a 'Looking For Group' callout in Discord"
            >
              ⚔️ Call for Tower Run
            </button>
          )}
        </div>
      ) : (
        <a href="/login" className="btn btn-secondary w-full" data-testid="map-login-link">
          Login to track progress
        </a>
      )}
    </div>
  );
}
